/**
 * Statement of Work production-line NDT: normalize legacy single `ndtType`
 * and new multi-select `ndtTypes` per station.
 */

export function ndtTypesFromStationEntry(entry) {
  if (!entry || typeof entry !== 'object') return [];
  const arr = entry.ndtTypes ?? entry.ndt_types;
  if (Array.isArray(arr)) {
    const out = [...new Set(arr.map(String).map((x) => x.trim()).filter(Boolean))];
    // Legacy compatibility: "Both" means RT + AUT
    if (out.length === 1 && String(out[0]).toUpperCase() === 'BOTH') return ['RT', 'AUT'];
    return out;
  }
  const single = entry.ndtType ?? entry.ndt_type;
  if (single == null || single === '') return [];
  const s = String(single).trim();
  if (s.toUpperCase() === 'BOTH') return ['RT', 'AUT'];
  if (s.includes(',')) {
    const parts = [...new Set(s.split(',').map((x) => x.trim()).filter(Boolean))];
    if (parts.length === 1 && String(parts[0]).toUpperCase() === 'BOTH') return ['RT', 'AUT'];
    return parts;
  }
  return [s];
}

/** Payload segment for one station — sends both `ndtTypes` and a legacy-friendly `ndtType`. */
export function ndtStationToPayload(stationNumber, ndtTypes) {
  const arr = Array.isArray(ndtTypes)
    ? [...new Set(ndtTypes.filter(Boolean).map(String))]
    : [];
  const payload = { stationNumber, ndtTypes: arr };
  if (arr.length === 0) payload.ndtType = '';
  else if (arr.length === 1) payload.ndtType = arr[0];
  else payload.ndtType = arr.join(', ');
  return payload;
}

/** Project-level `ndtType` may be a string, comma-separated string, or array (multi-select). */
export function projectNdtTypesArray(ndt) {
  if (ndt && typeof ndt === 'object' && !Array.isArray(ndt)) {
    const arr = ndt.ndtTypes ?? ndt.ndt_types;
    if (Array.isArray(arr) && arr.length > 0) {
      const out = [...new Set(arr.map(String).map((x) => x.trim()).filter(Boolean))];
      if (out.length === 1 && String(out[0]).toUpperCase() === 'BOTH') return ['RT', 'AUT'];
      return out;
    }
    const single = ndt.ndtType ?? ndt.ndt_type;
    return projectNdtTypesArray(single);
  }
  if (ndt == null || ndt === '') return [];
  if (Array.isArray(ndt)) return [...new Set(ndt.map(String).filter(Boolean))];
  const s = String(ndt).trim();
  if (s.toUpperCase() === 'BOTH') return ['RT', 'AUT'];
  if (s.includes(',')) {
    return [...new Set(s.split(',').map((x) => x.trim()).filter(Boolean))];
  }
  return [s];
}

export function formatProjectNdtTypesLabel(ndt) {
  const arr = projectNdtTypesArray(ndt);
  if (!arr.length) return '';
  return arr.join(', ');
}

/** Ant Design multi-select initial value from API (string, comma string, or array). */
export function ndtTypeFormInitial(ndt) {
  const arr = projectNdtTypesArray(ndt);
  return arr.length ? arr : undefined;
}

/** Normalize for project create/update payloads (null clears). */
export function normalizeNdtTypeForApi(v) {
  if (v == null) return null;
  if (Array.isArray(v)) return v.length ? v : null;
  const s = String(v).trim();
  return s === '' ? null : v;
}

/** Label for one entry in `ndtStations` (supports `ndtTypes` + legacy `ndtType`). */
export function formatNdtStationEntryLabel(entry) {
  if (!entry || typeof entry !== 'object') return '';
  const types = ndtTypesFromStationEntry(entry);
  return types.length ? types.join(', ') : '';
}
